import { useCallback, useState } from 'react';
import { isAdmin as isUserAdmin } from '../../common/redux/selectors/AccountSelectors';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { graphql } from '@apollo/client/react/hoc';
import AccountQueries from '../../common/graphql/Account';
import { userFragment } from '../../common/graphql/Fragments';
import { filter } from 'graphql-anywhere';
import { isFunction, join, noop } from 'lodash';
import { IUserInfo, IUpdateProfileRequest } from '../interfaces';
import Account from '../../common/redux/actions/AccountActions';
import { useToastProvider } from '../../components/toast/toast-launcher';
import { messages } from '../../i18n';
import { MutationFunction } from '@apollo/client/react/types/types';

interface IAccountMutationInputProps {
  isAdmin?: boolean;
  refreshUser?(user);
  setSaving?: (saving?: boolean) => any;
  onError?: (user: IUserInfo) => any;
}

export interface IAccountMutationOutputProps {
  updateUser(user: IUpdateProfileRequest): Promise<any>;
}

interface IAccountMutationData {
  updateUser: IUserInfo;
}

const accountMutationFuncs = (
  {
    isAdmin,
    refreshUser = noop,
    setSaving = noop,
    onError = noop,
  }: IAccountMutationInputProps,
  mutate: MutationFunction<IAccountMutationData, object>,
) => ({
  updateUser: (modifiedUser: IUserInfo): Promise<void> => {
    const user: IUserInfo = filter(userFragment, {
      ...modifiedUser,
      fullName: '',
    });
    if (isAdmin) {
      user.roles = modifiedUser.roles;
    }
    setSaving(true);
    return mutate({
      variables: {
        user,
      },
      refetchQueries: [{ query: AccountQueries.AccountQuery }],
    })
      .then(({ data }) => {
        // Update stored current user info
        if (isFunction(refreshUser)) {
          refreshUser(data.updateUser);
        }
      })
      .catch(error => {
        // show toast
        console.log('Error updating account.', error);
        onError(user);
      })
      .finally(() => setSaving(false));
  },
});

export const AccountMutation = graphql<
  IAccountMutationInputProps,
  IAccountMutationData,
  object,
  IAccountMutationOutputProps
>(AccountQueries.UpdateAccountMutation, {
  props: ({ ownProps = {}, mutate }) => accountMutationFuncs(ownProps, mutate),
});

export const useAccountMutation = (shouldRefreshCurrentUser = true) => {
  const [mutate] = useMutation(AccountQueries.UpdateAccountMutation);
  const [saving, setSaving] = useState<boolean>();
  const dispatch = useDispatch();
  const refreshUser = shouldRefreshCurrentUser
    ? user => {
        dispatch(Account.setCurrentUser(user));
      }
    : noop;
  const isAdmin = useSelector(({ account }: any) => isUserAdmin(account));

  const { showToast } = useToastProvider();

  const onError = useCallback(
    (user: IUserInfo) =>
      showToast({
        type: 'error',
        label: messages.administration.errorUpdatingAccount,
        children: (
          <span>
            {join([user?.givenName, user?.surname], ' ')}
            <br />
            <br />
            ID: {user?.id}
          </span>
        ),
      }),
    [showToast],
  );

  return {
    saving,
    ...accountMutationFuncs(
      { isAdmin, refreshUser, setSaving, onError },
      mutate,
    ),
  };
};
